import React from 'react';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Laugh, SmilePlus, Trophy, Star, MessageSquare } from 'lucide-react';

const MemeGalleryTab = ({ 
  topMemer, memeLeaderboard, memeFeed, memeOfTheDay, memeSort, setMemeSort,
  laughAtMeme, renderMedia, period 
}) => (
  <div className="space-y-6">
    {topMemer && (
      <Card className="p-5 bg-gradient-to-r from-green-400 to-emerald-500 text-white rounded-2xl" data-testid="top-memer-banner">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-white/20 rounded-xl"><SmilePlus className="w-8 h-8" /></div>
          <div>
            <p className="text-sm opacity-90">Top Memer</p>
            <p className="text-xl font-bold">{topMemer.user?.name || 'Unknown'}</p>
            <p className="text-sm opacity-80">@{topMemer.user?.username} · {topMemer.total_laughs} laughs · {topMemer.meme_count} memes</p>
          </div>
        </div>
      </Card>
    )}

    {memeOfTheDay && (
      <Card className="rounded-2xl overflow-hidden border-2 border-yellow-300 dark:border-yellow-600" data-testid="meme-of-the-day">
        <div className="p-4 bg-gradient-to-r from-yellow-400 via-amber-400 to-orange-400 text-white">
          <div className="flex items-center gap-2"><Star className="w-5 h-5" /><h3 className="text-lg font-bold">Meme of the Day</h3><Star className="w-5 h-5" /></div>
        </div>
        <div className="p-5 bg-white dark:bg-gray-800">
          <div className="flex items-start gap-3">
            <Avatar className="w-10 h-10 flex-shrink-0">
              <AvatarImage src={memeOfTheDay.author?.avatar_url} />
              <AvatarFallback>{memeOfTheDay.author?.name?.charAt(0) || '?'}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-bold text-sm text-gray-900 dark:text-white">{memeOfTheDay.author?.name}</span>
                <span className="text-xs text-gray-500">@{memeOfTheDay.author?.username}</span>
              </div>
              <p className="text-gray-800 dark:text-gray-200 text-sm mb-2">{memeOfTheDay.content}</p>
              {renderMedia(memeOfTheDay)}
              <div className="flex items-center gap-3 mt-3">
                <button onClick={() => laughAtMeme(memeOfTheDay.post_id)}
                  className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-bold transition-all ${memeOfTheDay.user_laughed ? 'bg-green-500 text-white shadow-lg' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-green-100'}`}
                  data-testid="laugh-motd">
                  <Laugh className="w-4 h-4" /><span>{memeOfTheDay.laugh_count || 0}</span>
                </button>
                <span className="text-xs text-gray-400">{memeOfTheDay.likes_count || 0} likes · {memeOfTheDay.comments_count || 0} comments</span>
              </div>
            </div>
          </div>
        </div>
      </Card>
    )}

    <Card className="p-4 rounded-2xl border-2 border-dashed border-green-200 dark:border-green-800" data-testid="meme-how-to">
      <div className="flex items-center gap-3">
        <div className="p-2 bg-green-100 dark:bg-green-900/40 rounded-xl"><SmilePlus className="w-5 h-5 text-green-600" /></div>
        <div>
          <h3 className="font-bold text-sm text-gray-900 dark:text-white">How to get featured</h3>
          <p className="text-xs text-gray-500">Include <span className="font-bold text-green-600">#memegallery</span> in your post and it will automatically appear here!</p>
        </div>
      </div>
    </Card>

    <div className="flex items-center gap-2">
      <span className="text-sm text-gray-500 font-medium">Sort by:</span>
      <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-full p-1">
        <button onClick={() => setMemeSort('recent')} className={`px-4 py-1.5 rounded-full text-xs font-bold transition-colors ${memeSort === 'recent' ? 'bg-white dark:bg-gray-700 text-green-600 shadow-sm' : 'text-gray-500'}`} data-testid="meme-sort-recent">Newest</button>
        <button onClick={() => setMemeSort('top')} className={`px-4 py-1.5 rounded-full text-xs font-bold transition-colors ${memeSort === 'top' ? 'bg-white dark:bg-gray-700 text-green-600 shadow-sm' : 'text-gray-500'}`} data-testid="meme-sort-top">Top Laughed</button>
      </div>
    </div>

    <Card className="rounded-2xl overflow-hidden" data-testid="meme-leaderboard">
      <div className="p-5 bg-gradient-to-r from-green-500 to-emerald-600 text-white">
        <h3 className="text-lg font-bold flex items-center gap-2"><Trophy className="w-5 h-5 text-yellow-300" /> Top 10 Memes · {period === 'month' ? 'This Month' : 'All Time'}</h3>
      </div>
      <div className="divide-y dark:divide-gray-700">
        {memeLeaderboard.length === 0 ? (
          <div className="p-8 text-center text-gray-500" data-testid="meme-leaderboard-empty"><SmilePlus className="w-10 h-10 mx-auto mb-2 text-gray-300" /><p>No memes yet. Post with #memegallery to get started!</p></div>
        ) : memeLeaderboard.map((meme) => (
          <div key={meme.post_id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition" data-testid={`meme-rank-${meme.rank}`}>
            <div className="flex items-start gap-3">
              <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${meme.rank === 1 ? 'bg-yellow-400 text-yellow-900' : meme.rank === 2 ? 'bg-gray-300 text-gray-700' : meme.rank === 3 ? 'bg-orange-300 text-orange-800' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'}`}>{meme.rank}</div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold text-sm">{meme.author?.name || 'Unknown'}</span>
                  <span className="text-xs text-gray-500">@{meme.author?.username}</span>
                </div>
                <p className="text-gray-800 dark:text-gray-200 text-sm mb-1">{meme.content}</p>
                {renderMedia(meme)}
              </div>
              <button onClick={() => laughAtMeme(meme.post_id)}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-bold transition-all ${meme.user_laughed ? 'bg-green-500 text-white shadow-lg' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-green-100'}`}
                data-testid={`laugh-meme-${meme.post_id}`}>
                <Laugh className="w-4 h-4" /><span>{meme.laugh_count || 0}</span>
              </button>
            </div>
          </div>
        ))}
      </div>
    </Card>

    {memeFeed.length > 0 && (
      <Card className="rounded-2xl overflow-hidden" data-testid="meme-feed">
        <div className="p-4 border-b dark:border-gray-700"><h3 className="font-bold flex items-center gap-2"><MessageSquare className="w-4 h-4" /> Latest Memes</h3></div>
        <div className="divide-y dark:divide-gray-700">
          {memeFeed.map((meme) => (
            <div key={meme.post_id} className="p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition" data-testid={`meme-feed-${meme.post_id}`}>
              <div className="flex items-start gap-3">
                <Avatar className="w-9 h-9 flex-shrink-0"><AvatarImage src={meme.author?.avatar_url} /><AvatarFallback>{meme.author?.name?.charAt(0) || '?'}</AvatarFallback></Avatar>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-sm">{meme.author?.name}</span>
                    <span className="text-xs text-gray-500">@{meme.author?.username}</span>
                  </div>
                  <p className="text-gray-800 dark:text-gray-200 text-sm">{meme.content}</p>
                  {renderMedia(meme)}
                </div>
                <button onClick={() => laughAtMeme(meme.post_id)}
                  className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold ${meme.user_laughed ? 'bg-green-500 text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600'}`}
                  data-testid={`laugh-feed-${meme.post_id}`}>
                  <Laugh className="w-3.5 h-3.5" /> {meme.laugh_count || 0}
                </button>
              </div>
            </div>
          ))}
        </div>
      </Card>
    )}
  </div>
);

export default MemeGalleryTab;
